import { noop } from 'underscore';
import React, { useCallback, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Stack, TextField } from '@fluentui/react';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import {
  getUrlPrefixAndDestination,
  getUpdatedFullUrlOnPrefixUpdate,
  getUpdatedFullUrlOnTextUpdate,
} from './utils';
import { validateAdUrl, UrlLengthLimit } from './validation';

const urlPrefixOptions = [
  { key: 'https', text: 'https://', title: 'https://' },
  { key: 'http', text: 'http://', title: 'http://' },
];

export function UrlInputFluent({
  url,
  i18n,
  onChange,
  containerClassName,
  additionalValidation,
  customizedValidation,
  prepopulateUrl,
  dropdownOptionAriaLabel,
  propertyKey,
  validateOnRender,
  additionalProperties,
  textFieldAriaLabel,
  customUrlProtocol,
  enableSpecialCharactersValidation,
  hideProtocol,
}) {
  const activity = useContext(ActivityContext);
  const [urlPrefix, urlWithoutPrefix] = useMemo(
    () => getUrlPrefixAndDestination(url),
    [url]
  );

  const validateAndUpdateUrl = useCallback((currentURL) => {
    // first check URL validity. If valid then validate domain by need.
    const validateFun = customizedValidation || validateAdUrl;
    let finalUrlValidatorError = validateFun(currentURL, '', i18n, UrlLengthLimit, customUrlProtocol, enableSpecialCharactersValidation);
    if (!finalUrlValidatorError && additionalValidation) {
      finalUrlValidatorError = additionalValidation(currentURL);
    }

    onChange(finalUrlValidatorError ? [finalUrlValidatorError] : [], currentURL);
    if (activity) {
      activity.signal(`on${propertyKey}Change - ${JSON.stringify(currentURL)} - isValid: ${!finalUrlValidatorError}}`);
    }
  }, [activity, additionalValidation, customUrlProtocol, customizedValidation, i18n, onChange, propertyKey]);

  const onUrlPrefixChanged = useCallback(
    (event, newItem) =>
      validateAndUpdateUrl(getUpdatedFullUrlOnPrefixUpdate(urlWithoutPrefix, newItem.key)),
    [urlWithoutPrefix, validateAndUpdateUrl]
  );

  const onCustomUrlProtocolChanged = useCallback(
    (event, value) =>
      validateAndUpdateUrl(getUpdatedFullUrlOnPrefixUpdate(urlWithoutPrefix, value)),
    [urlWithoutPrefix, validateAndUpdateUrl]
  );

  const onTextChange = useCallback((event, value) => {
    const newValue = getUpdatedFullUrlOnTextUpdate(urlPrefix, value);

    if (newValue) {
      validateAndUpdateUrl(getUpdatedFullUrlOnTextUpdate(urlPrefix, value));
    }
  }, [validateAndUpdateUrl, urlPrefix]);

  useEffect(() => {
    if (!url && prepopulateUrl) {
      // Prepopulate final url with prepopulate on load if no value is provided.
      validateAndUpdateUrl(getUpdatedFullUrlOnTextUpdate(urlPrefix, prepopulateUrl));
    } else if (validateOnRender) {
      validateAndUpdateUrl(url);
    }
    // only validate on render and prepopulate on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <Stack
      className={containerClassName}
      horizontal
      tokens={{
        childrenGap: 16,
      }}
    >
      {
        !hideProtocol && customUrlProtocol &&
          <TextField
            value={urlPrefix}
            onChange={onCustomUrlProtocolChanged}
            className="url-protocol"
            style={{ width: '100px' }}
            suffix="://"
          />
      }
      {
        !hideProtocol && !customUrlProtocol &&
          <Dropdown
            options={urlPrefixOptions}
            selectedKey={urlPrefix}
            onChange={onUrlPrefixChanged}
            ariaLabel={dropdownOptionAriaLabel}
            className="url-protocol"
          />
      }
      <Stack.Item grow>
        <TextField
          value={urlWithoutPrefix}
          onChange={onTextChange}
          className="url-field"
          ariaLabel={textFieldAriaLabel}
          {... additionalProperties}
        />
      </Stack.Item>
    </Stack>
  );
}

UrlInputFluent.propTypes = {
  containerClassName: PropTypes.string,
  url: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  additionalValidation: PropTypes.func,
  customizedValidation: PropTypes.func,
  prepopulateUrl: PropTypes.string,
  dropdownOptionAriaLabel: PropTypes.string,
  propertyKey: PropTypes.string,
  validateOnRender: PropTypes.bool,
  additionalProperties: PropTypes.shape({}),
  textFieldAriaLabel: PropTypes.string.isRequired,
  customUrlProtocol: PropTypes.bool,
  enableSpecialCharactersValidation: PropTypes.bool,
  hideProtocol: PropTypes.bool,
};

UrlInputFluent.defaultProps = {
  onChange: noop,
  onClick: noop,
  containerClassName: '',
  additionalValidation: noop,
  customizedValidation: null,
  prepopulateUrl: '',
  dropdownOptionAriaLabel: '',
  propertyKey: '',
  validateOnRender: false,
  additionalProperties: {},
  customUrlProtocol: false,
  enableSpecialCharactersValidation: false,
  hideProtocol: false,
};
